"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.StateRules = exports.FederalOnlyRules = exports.FederalRules = exports.FEDERAL_READABLE_GENERATOR_STATUSES = exports.JurisdictionRules = void 0;
const wasteAccumulation_1 = require("encamp-shared/dist/constants/wasteAccumulation");
const unwrap_1 = require("../../unwrap");
const onSiteAccumulationThresholds_1 = require("../onSiteAccumulationThresholds");
/**
 * Note that these classes are meant to be singletons instantiated at module load;
 * they are classes because the partial-override ergonomics of TS classes fit the
 * domain we need to model surprisingly well.
 * They should never carry any instance state, only static constants and pure functions.
 *
 * Concerned only with "routine" limits - Subpart L / episodics are handled
 * elsewhere and should be composed together with this by caller
 */
class JurisdictionRules {
    constructor() {
        // Facts derived from multiple raw context params
        this.derivations = {};
    }
    resolve(ctx) {
        const jurisdictionCtx = this.narrowToJurisdiction(ctx);
        if (jurisdictionCtx.areaType === "CENTRAL") {
            return { areaType: "CENTRAL", clock: this.centralClock(jurisdictionCtx) };
        }
        return {
            areaType: "SATELLITE",
            caps: this.satelliteCaps(jurisdictionCtx),
            clock: this.satelliteClock(jurisdictionCtx),
        };
    }
    /**
     * The facility-wide on-site accumulation thresholds — a fourth slot with a
     * result shape of their own, because a threshold that sums every container at
     * the site is not an area-scoped answer (see ../onSiteCaps.ts).
     */
    resolveAccumulationThresholds(ctx) {
        return this.accumulationThresholds(this.narrowAccumulationThresholdContext(ctx));
    }
}
exports.JurisdictionRules = JurisdictionRules;
/**
 * The federal generator statuses as the threshold result names them. A state
 * whose status IS the federal one reuses these; a state with a status name of
 * its own (Connecticut's CESQG) authors its own entry beside them.
 */
exports.FEDERAL_READABLE_GENERATOR_STATUSES = {
    LQG: { abbreviation: "LQG", name: "Large Quantity Generator" },
    SQG: { abbreviation: "SQG", name: "Small Quantity Generator" },
    VSQG: { abbreviation: "VSQG", name: "Very Small Quantity Generator" },
};
/**
 * FEDERAL ACCUMULATION THRESHOLD EXPECTATIONS — 40 CFR 262.14, 262.16
 *
 * Literal numbers, for the same reason as the area table: this is what pins
 * the constants. Federal law reads no state generator status, so every row spans it.
 */
const FEDERAL_ACCUMULATION_THRESHOLD_EXPECTATIONS = [
    {
        when: { generatorStatus: "LQG" },
        expect: {
            thresholds: { totalKg: null, nonAcuteKg: null, acuteKg: null },
            judgedAgainst: { abbreviation: "LQG", name: "Large Quantity Generator" },
        },
        note: "An LQG is time-limited (262.17); no accumulation threshold applies.",
    },
    {
        when: { generatorStatus: "SQG" },
        expect: {
            thresholds: { totalKg: 6000, nonAcuteKg: null, acuteKg: null },
            judgedAgainst: { abbreviation: "SQG", name: "Small Quantity Generator" },
        },
        note: "262.16(b)(1): a single 6,000 kg total across acute and non-acute waste.",
    },
    {
        when: { generatorStatus: "VSQG" },
        expect: {
            thresholds: { totalKg: null, nonAcuteKg: 1000, acuteKg: 1 },
            judgedAgainst: { abbreviation: "VSQG", name: "Very Small Quantity Generator" },
        },
        note: (0, unwrap_1.unwrap) `262.14(a)(3)-(4). KNOWN GAP (ENG-17590): 100 kg threshold for soil/water/debris
    contaminated by acute haz waste spill is not currently modeled.`,
    },
    {
        when: { generatorStatus: null },
        expect: { thresholds: { totalKg: null, nonAcuteKg: null, acuteKg: null }, judgedAgainst: null },
        note: "No status, no thresholds: the caller prompts for setup rather than assuming a status.",
    },
];
/**
 * FEDERAL EXPECTATIONS
 *
 * Numbers and citations are literal rather than imported: this table is what
 * pins the implementation's constants, so a change to one must fail here.
 */
const FEDERAL_SATELLITE_CAPS = {
    kind: "VOLUME",
    nonAcuteGal: 55,
    acuteGal: 0.25,
    acuteKg: 1,
    moveWindowDays: 3,
    citation: "40 CFR 262.15",
    jurisdiction: "FEDERAL",
};
/**
 * 262.15 imposes no standing satellite clock — the only time limit is the
 * move window once a cap is breached, carried as moveWindowDays on the caps.
 */
const NO_STANDING_SATELLITE_CLOCK = {
    kind: "NO_LIMIT",
    citation: "40 CFR 262.15",
    jurisdiction: "FEDERAL",
};
const FEDERAL_DERIVATIONS = {
    effectiveWasteKind: {
        from: ["wasteKind", "managedAsUniversalWaste", "operatesUnderSubpartP"],
        label: "Federal Effective Subclassification",
        citation: "40 CFR 273.9, 261.6(a)(4), 266.501",
        description: (0, unwrap_1.unwrap) `Which management program governs a container. The UNIVERSAL_WASTE value 
      coming in says only that this could be UW somewhere. A category the state never adopted is not universal
      waste there: part 273's standards apply only "while managed as UW" (40 CFR 273.9,
      262.13(c)(6)). Whether the relevant state program treats it as UW is taken as a second input here. 
      
      Used oil is governed by part 279.
      
      Pharmaceutical waste is treated specially only at facilities that operate under Subpart P,
      which like UW is taken as an additional input. Unlike UW, Subpart P varies by facility, not
      only by state. A facility not operating under it manages pharmaceutical waste under
      the ordinary generator standards.`,
        cases: [
            {
                when: { wasteKind: "UNIVERSAL_WASTE", managedAsUniversalWaste: true },
                produces: "UNIVERSAL_WASTE",
            },
            {
                when: { wasteKind: "UNIVERSAL_WASTE", managedAsUniversalWaste: false },
                produces: "ORDINARY",
            },
            {
                when: { wasteKind: "USED_OIL" },
                produces: "USED_OIL",
            },
            {
                when: { wasteKind: "PHARMACEUTICAL", operatesUnderSubpartP: true },
                produces: "SUBPART_P_PHARMACEUTICAL",
            },
            {
                when: { wasteKind: "PHARMACEUTICAL", operatesUnderSubpartP: false },
                produces: "ORDINARY",
            },
            {
                when: { wasteKind: null },
                produces: "ORDINARY",
            },
        ],
    },
};
const CREDITABLE_PHARMA_NOTE = (0, unwrap_1.unwrap) `
      266.502(f)(1) gives a one-year limit for non-creditable
      pharmaceuticals. Creditable pharmaceuticals have no clock,
      but we don't currently distinguish creditable from non-creditable. 
      We assume the stricter reading.`;
const FEDERAL_EXPECTATIONS = [
    // ── CENTRAL ACCUMULATION ──────────────────────────────────────────────
    // Waste handled by ordinary rules
    // VSQG — 40 CFR 262.14
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: {
            areaType: "CENTRAL",
            generatorStatus: "VSQG",
        },
        expect: {
            clock: { kind: "NO_LIMIT", citation: "40 CFR 262.14", jurisdiction: "FEDERAL" },
        },
    },
    // SQG — 40 CFR 262.16
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: {
            areaType: "CENTRAL",
            generatorStatus: "SQG",
            mustTransport200Miles: false,
        },
        expect: {
            clock: { kind: "TIME", days: 180, citation: "40 CFR 262.16(b)", jurisdiction: "FEDERAL" },
        },
    },
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: {
            areaType: "CENTRAL",
            generatorStatus: "SQG",
            mustTransport200Miles: true,
        },
        expect: {
            clock: { kind: "TIME", days: 270, citation: "40 CFR 262.16(c)", jurisdiction: "FEDERAL" },
        },
    },
    // LQG — 40 CFR 262.17
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: {
            areaType: "CENTRAL",
            generatorStatus: "LQG",
        },
        expect: {
            clock: { kind: "TIME", days: 90, citation: "40 CFR 262.17(a)", jurisdiction: "FEDERAL" },
        },
    },
    // Unknown status
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: {
            areaType: "CENTRAL",
            generatorStatus: null,
        },
        expect: { clock: { kind: "UNDETERMINED" } },
    },
    // ── SATELLITE ACCUMULATION — 40 CFR 262.15 ────────────────────────────
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: {
            areaType: "SATELLITE",
            generatorStatus: "SQG",
        },
        expect: { caps: FEDERAL_SATELLITE_CAPS, clock: NO_STANDING_SATELLITE_CLOCK },
    },
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: {
            areaType: "SATELLITE",
            generatorStatus: "LQG",
        },
        expect: { caps: FEDERAL_SATELLITE_CAPS, clock: NO_STANDING_SATELLITE_CLOCK },
    },
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: {
            areaType: "SATELLITE",
            generatorStatus: "VSQG",
        },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "40 CFR 262.14", jurisdiction: "FEDERAL" },
            clock: NO_STANDING_SATELLITE_CLOCK,
        },
    },
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: {
            areaType: "SATELLITE",
            generatorStatus: null,
        },
        expect: { caps: FEDERAL_SATELLITE_CAPS, clock: NO_STANDING_SATELLITE_CLOCK },
        note: "Take the stricter reading until we know the generator status.",
    },
    // ── KIND-SPECIFIC PROGRAMS ─────────────────────────────────────────────
    // Universal waste, used oil, and Subpart P pharmaceutical containers are
    // governed by their own part of 40 CFR rather than part 262's
    // generator-status-based clocks.
    {
        given: { effectiveWasteKind: "UNIVERSAL_WASTE" },
        when: {
            areaType: "CENTRAL",
        },
        expect: {
            clock: {
                kind: "TIME",
                days: 365,
                citation: "40 CFR 273.15(a), 273.35(a)",
                jurisdiction: "FEDERAL",
            },
        },
        note: (0, unwrap_1.unwrap) `
      273.15(a) (small-quantity handler) and 273.35(a) (large-quantity handler) are word for
      word identical one-year limits. We don't distinguish handler categories, so cite both.`,
    },
    {
        given: { effectiveWasteKind: "UNIVERSAL_WASTE" },
        when: {
            areaType: "SATELLITE",
        },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "40 CFR 261.9, 273.1(b)", jurisdiction: "FEDERAL" },
            clock: {
                kind: "TIME",
                days: 365,
                citation: "40 CFR 273.15(a), 273.35(a)",
                jurisdiction: "FEDERAL",
            },
        },
    },
    {
        given: { effectiveWasteKind: "USED_OIL" },
        when: {
            areaType: "CENTRAL",
        },
        expect: {
            clock: { kind: "NO_LIMIT", citation: "40 CFR 279.22", jurisdiction: "FEDERAL" },
        },
        note: (0, unwrap_1.unwrap) `35-day cap for transfer facilities (not modeled in our system), none for generators.`,
    },
    {
        given: { effectiveWasteKind: "USED_OIL" },
        when: {
            areaType: "SATELLITE",
        },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "40 CFR 261.6(a)(4)", jurisdiction: "FEDERAL" },
            clock: { kind: "NO_LIMIT", citation: "40 CFR 279.22", jurisdiction: "FEDERAL" },
        },
    },
    // Subpart P is facility-scoped. A non-participating
    // facility's pharmaceutical containers land on the ORDINARY rows above.
    {
        given: { effectiveWasteKind: "SUBPART_P_PHARMACEUTICAL" },
        when: {
            areaType: "CENTRAL",
        },
        expect: {
            clock: { kind: "TIME", days: 365, citation: "40 CFR 266.502(f)(1)", jurisdiction: "FEDERAL" },
        },
        note: CREDITABLE_PHARMA_NOTE,
    },
    {
        given: { effectiveWasteKind: "SUBPART_P_PHARMACEUTICAL" },
        when: {
            areaType: "SATELLITE",
        },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "40 CFR 266.501(d)", jurisdiction: "FEDERAL" },
            clock: { kind: "TIME", days: 365, citation: "40 CFR 266.502(f)(1)", jurisdiction: "FEDERAL" },
        },
        note: CREDITABLE_PHARMA_NOTE,
    },
];
/**
 * Federal RCRA generator accumulation limits, 40 CFR part 262 — and the
 * behavior every state class starts from (see `StateRules`).
 *
 * Every result stamps `jurisdiction` as a LITERAL matching the citation
 * beside it. Deliberately no `this.jurisdiction` member to DRY that up: an
 * inherited or `super.`-delegated method keeps the subclass's `this`, so a
 * federal answer reached through a state class would come back stamped with
 * the state's jurisdiction next to a CFR citation. Federal methods stamping
 * FEDERAL regardless of the instance is what keeps a state's inherited slots
 * honestly labeled.
 */
class FederalRules extends JurisdictionRules {
    constructor() {
        super(...arguments);
        this.expectations = FEDERAL_EXPECTATIONS;
        // Widened to `Derivations` deliberately: a state class overrides this with
        // its own table, which the literal type of the federal one would refuse.
        this.derivations = FEDERAL_DERIVATIONS;
        // Federal law recognizes no state tier, so it refuses none either.
        this.stateStatusDomain = [];
        this.accumulationThresholdExpectations = FEDERAL_ACCUMULATION_THRESHOLD_EXPECTATIONS;
    }
    accumulationThresholds(ctx) {
        return {
            thresholds: (0, onSiteAccumulationThresholds_1.getFederalAccumulationThresholds)(ctx.generatorStatus),
            judgedAgainst: ctx.generatorStatus === null
                ? null
                : exports.FEDERAL_READABLE_GENERATOR_STATUSES[ctx.generatorStatus],
        };
    }
    centralClock(ctx) {
        if (ctx.wasteKind === "UNIVERSAL_WASTE" && ctx.managedAsUniversalWaste) {
            /** Universal waste (273.15(a), 273.35(a)) is scoped out of part 262 entirely
             *
             * 273.15(a) is the small-quantity handler limit, 273.35(a) the
             * large-quantity handler limit — word for word identical. This engine
             * doesn't model handler category (set by UW quantity on hand, not
             * generator status), hence citing both together.
             */
            return {
                kind: "TIME",
                days: 365,
                citation: "40 CFR 273.15(a), 273.35(a)",
                jurisdiction: "FEDERAL",
            };
        }
        if (ctx.wasteKind === "PHARMACEUTICAL" && ctx.operatesUnderSubpartP) {
            /**
             * 266.501(d) puts Subpart P "in lieu of parts 262 through 265" ONLY at a facility that
             * operates under it (a recorded healthcare-facility activity, no
             * withdrawal, in an adopting state).
             *
             * The healthcare-facility limit for non-creditable pharmaceuticals.
             * Creditable pharmaceuticals have no facility accumulation clock at
             * all, but this engine's wasteKind doesn't distinguish creditable from
             * non-creditable, so applying one year to every PHARMACEUTICAL
             * container is over-strict but never under-strict.
             */
            return { kind: "TIME", days: 365, citation: "40 CFR 266.502(f)(1)", jurisdiction: "FEDERAL" };
        }
        if (ctx.wasteKind === "USED_OIL") {
            // Part 279 sets no generator storage time limit — only a 35-day cap
            // for transfer facilities, which doesn't apply here.
            return { kind: "NO_LIMIT", citation: "40 CFR 279.22", jurisdiction: "FEDERAL" };
        }
        if (ctx.generatorStatus === "LQG") {
            return { kind: "TIME", days: 90, citation: "40 CFR 262.17(a)", jurisdiction: "FEDERAL" };
        }
        if (ctx.generatorStatus === "SQG") {
            if (ctx.mustTransport200Miles) {
                return { kind: "TIME", days: 270, citation: "40 CFR 262.16(c)", jurisdiction: "FEDERAL" };
            }
            return { kind: "TIME", days: 180, citation: "40 CFR 262.16(b)", jurisdiction: "FEDERAL" };
        }
        if (ctx.generatorStatus === "VSQG") {
            // A VSQG under 262.14 is not subject to 262.16/262.17's central time
            // limits, and 262.14 imposes none of its own; what constrains a VSQG is
            // the facility-wide on-site cap, not a container-specific limit minted on
            // all VSQG containers.
            return { kind: "NO_LIMIT", citation: "40 CFR 262.14", jurisdiction: "FEDERAL" };
        }
        return {
            kind: "UNDETERMINED",
            missingAxes: ["generatorStatus"],
            reason: (0, unwrap_1.unwrap) `
        Storage deadlines need this facility's generator status, which isn't known yet.
        Generator status comes from the facility's EPA registration or its shipment history.`,
        };
    }
    satelliteCaps(ctx) {
        // Universal waste and used oil are scoped out of part 262 entirely, so
        // the SAA volume caps and move window never apply regardless of
        // generator status.
        if (ctx.wasteKind === "UNIVERSAL_WASTE" && ctx.managedAsUniversalWaste) {
            return { kind: "NO_LIMIT", citation: "40 CFR 261.9, 273.1(b)", jurisdiction: "FEDERAL" };
        }
        if (ctx.wasteKind === "USED_OIL") {
            return { kind: "NO_LIMIT", citation: "40 CFR 261.6(a)(4)", jurisdiction: "FEDERAL" };
        }
        if (ctx.wasteKind === "PHARMACEUTICAL" && ctx.operatesUnderSubpartP) {
            return { kind: "NO_LIMIT", citation: "40 CFR 266.501(d)", jurisdiction: "FEDERAL" };
        }
        if (ctx.generatorStatus === "VSQG") {
            return { kind: "NO_LIMIT", citation: "40 CFR 262.14", jurisdiction: "FEDERAL" };
        }
        // An unknown status still gets the caps — the more protective default.
        return {
            kind: "VOLUME",
            nonAcuteGal: wasteAccumulation_1.SAA_NON_ACUTE_VOLUME_LIMIT_GAL,
            acuteGal: wasteAccumulation_1.SAA_ACUTE_VOLUME_LIMIT_GAL,
            acuteKg: wasteAccumulation_1.SAA_ACUTE_MASS_LIMIT_KG,
            moveWindowDays: wasteAccumulation_1.SATELLITE_MOVE_WINDOW_DAYS,
            citation: "40 CFR 262.15",
            jurisdiction: "FEDERAL",
        };
    }
    satelliteClock(ctx) {
        if (ctx.wasteKind === "UNIVERSAL_WASTE" && ctx.managedAsUniversalWaste) {
            return {
                kind: "TIME",
                days: 365,
                citation: "40 CFR 273.15(a), 273.35(a)",
                jurisdiction: "FEDERAL",
            };
        }
        if (ctx.wasteKind === "PHARMACEUTICAL" && ctx.operatesUnderSubpartP) {
            return { kind: "TIME", days: 365, citation: "40 CFR 266.502(f)(1)", jurisdiction: "FEDERAL" };
        }
        if (ctx.wasteKind === "USED_OIL") {
            return { kind: "NO_LIMIT", citation: "40 CFR 279.22", jurisdiction: "FEDERAL" };
        }
        // 262.15 sets no standing clock on satellite accumulation for ordinary waste — the only time
        // limit is the exceedance-triggered move window, carried on the caps.
        return { kind: "NO_LIMIT", citation: "40 CFR 262.15", jurisdiction: "FEDERAL" };
    }
}
exports.FederalRules = FederalRules;
/**
 * Exists because `FederalRules` above reads a generic context — that is what
 * lets a state inherit — and a generic class cannot say how to read a context
 * whose type is still a parameter.
 */
class FederalOnlyRules extends FederalRules {
    narrowToJurisdiction(ctx) {
        return ctx;
    }
    narrowAccumulationThresholdContext(ctx) {
        return { generatorStatus: ctx.generatorStatus };
    }
}
exports.FederalOnlyRules = FederalOnlyRules;
/**
 * Behavior inherits; the spec never does. A state overrides only the slots
 * where its law departs from federal — inherited slots return FEDERAL-stamped
 * answers, the honest reading of "this state falls back to federal law here"
 * — but it must author its own complete expectations table, including rows
 * claiming those inherited federal answers. Were `expectations` inherited
 * too, a half-written state would pass the sweep with federal rows
 * masquerading as reviewed state claims. `derivations` is abstract for the
 * same reason: inheriting the federal table would let a state's rows expand
 * through federal routing, with the export printing federal determinations
 * under the state's heading.
 */
class StateRules extends FederalRules {
    isValidStateGeneratorStatus(status) {
        return this.stateStatusDomain.some((domain) => domain === status);
    }
    // A stored stateGeneratorStatus that this state's law does not define is
    // data corruption — treat same as "could not be determined"
    narrowToJurisdiction(ctx) {
        if (this.isValidStateGeneratorStatus(ctx.stateGeneratorStatus)) {
            return { ...ctx, stateGeneratorStatus: ctx.stateGeneratorStatus };
        }
        return { ...ctx, stateGeneratorStatus: null };
    }
    narrowAccumulationThresholdContext(ctx) {
        if (this.isValidStateGeneratorStatus(ctx.stateGeneratorStatus)) {
            return {
                generatorStatus: ctx.generatorStatus,
                stateGeneratorStatus: ctx.stateGeneratorStatus,
            };
        }
        return { generatorStatus: ctx.generatorStatus, stateGeneratorStatus: null };
    }
}
exports.StateRules = StateRules;
//# sourceMappingURL=FEDERAL.js.map